import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Banner } from '@src/app/shared/components/ui-components/banner-carousel/interfaces/banner';
import { environment } from '@src/environments/environment';
import { of, Observable, forkJoin } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { BannerService } from '@src/app/core/services/banner.service';
import { BannerItemService } from '@src/app/core/services/banner-item.service';
import { EItemLocation } from '@src/app/core/models/cms/banner-enums';

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private http = inject(HttpClient);
  private bannerService = inject(BannerService);
  private bannerItemService = inject(BannerItemService);
  private urlDynamicAssets: string = 'assets/client-assets';

  loadColors(): Promise<void> {
    return this.http
      .get<any>(
        //`assets/instance-configs/${environment.currentInstance}/layout/visual-identity/variables/variables.json`,
        `${this.urlDynamicAssets}/layout/style/variables.json`,
      )
      .toPromise()
      .then((data) => {
        const colors = data;
        if (colors) {
          this.applyColors(colors);
        } else {
          console.warn(`No colors defined for instance: ${environment.currentInstance}`);
        }
      });
  }

  private applyColors(colors: { [key: string]: string }) {
    const root = document.documentElement;
    Object.keys(colors).forEach((key) => {
      root.style.setProperty(`--${key}`, colors[key]);
    });
  }

  private isBannerItemInValidDateRange(item: { startDate?: string | null; endDate?: string | null }): boolean {
    const now = Date.now();
    const start = item.startDate ? new Date(item.startDate).getTime() : null;
    const end = item.endDate ? new Date(item.endDate).getTime() : null;
    if (start != null && now < start) return false;
    if (end != null && now > end) return false;
    return true;
  }

  getBannersForHome(): Observable<Banner[]> {
    return this.bannerService
      .get({ page: 1, pageSize: 50 })
      .pipe(
        switchMap((res) => {
          const homeBanners = (res.items || []).filter(
            (b) => b.itemLocation === EItemLocation.Home && b.isActive === true
          );
          if (homeBanners.length === 0) {
            return of([]);
          }
          return forkJoin(
            homeBanners.map((b) =>
              this.bannerItemService.getByBannerId(b.id).pipe(
                map((items) => (Array.isArray(items) ? items : []))
              )
            )
          ).pipe(
            switchMap((itemsArrays) => {
              const allItems: { order: number; id: number; imageFileId?: number; imageUrl: string | null; imageAltText: string | null; urlLink: string | null }[] = [];
              itemsArrays.forEach((items) => {
                items
                  .filter(
                    (item) =>
                      item.isActive &&
                      ((item.imageFileId ?? 0) > 0 || item.imageUrl) &&
                      this.isBannerItemInValidDateRange(item)
                  )
                  .forEach((item) =>
                    allItems.push({
                      order: item.order,
                      id: item.id,
                      imageFileId: item.imageFileId,
                      imageUrl: item.imageUrl ?? null,
                      imageAltText: item.imageAltText,
                      urlLink: item.urlLink,
                    })
                  );
              });
              allItems.sort((a, b) => a.order - b.order);
              if (allItems.length === 0) {
                return of([]);
              }
              const imageUrls$ = allItems.map((item) => {
                const imageId = item.imageFileId ?? 0;
                if (imageId > 0) {
                  return this.bannerService.getImage(imageId).pipe(
                    map((img) => `data:${img.contentType};base64,${img.base64}`),
                    catchError(() => of(null))
                  );
                }
                return of(item.imageUrl);
              });
              return forkJoin(imageUrls$).pipe(
                map((urls) =>
                  allItems
                    .map((item, i) => ({ item, url: urls[i] }))
                    .filter(({ url }) => url != null && url !== '')
                    .map(
                      ({ item, url }): Banner => ({
                        id: item.id,
                        imageUrl: url!,
                        altText: item.imageAltText ?? undefined,
                        link: item.urlLink ?? undefined,
                      })
                    )
                )
              );
            })
          );
        }),
        catchError(() => of([]))
      );
  }

  getBannersFromAssets(): Observable<Banner[]> {
    return of([
      {
        id: 1,
        imageUrl: `${this.urlDynamicAssets}/layout/images/carousel-dashboard/banner-home1.jpg`,
        altText: 'Banner Promocional 1',
      },
      {
        id: 2,
        imageUrl: `${this.urlDynamicAssets}/layout/images/carousel-dashboard/banner-home1.jpg`,
        altText: 'Banner Promocional 2',
      },
      {
        id: 3,
        imageUrl: `${this.urlDynamicAssets}/layout/images/carousel-dashboard/banner-home1.jpg`,
        altText: 'Banner Promocional 3',
      },
    ]);
  }

  getEmptyBanners(): Banner[] {
    return [];
  }
}
